// Front-end theme selection.
//
// The ENGINE owns almost everything a theme is - every asset path and colour lives in
// adaptive-practice-engine's themes/*.json and is chosen by the 2nd argument to
// loadSkillBySkillId / loadSkillByFileName. This module covers only the handful of things the
// APP paints itself, which space.css hardcodes to the space folder and the engine can't reach.
//
// Ported from adaptive-practice/src/theme.ts. Two things were dropped in the port:
//   - TAB_THEME / getThemeName(), which resolve a theme from localStorage 'activeTab'.
//     Play UI has no activeTab and has a separate component per mode, so the mode IS the
//     component and the theme is passed at the call site instead.
//   - the Settings picker helpers, since Play UI has no Settings screen yet. When the
//     change-theme option lands, THEMES / getPickedTheme / setThemeName come across with it.

/**
 * Where the served assets live, as a ROOT-relative prefix.
 *
 * Not "./". These paths end up in two places that resolve relatives differently:
 *
 *   - CSS custom properties. applyTheme sets --theme-mainbg as an inline style on <html>, but
 *     the rule that USES it (background-image: var(--theme-mainbg)) sits in the bundled
 *     stylesheet. The browser resolves the substituted url() against THAT stylesheet, so a
 *     relative path became /static/css/assets/... and 404'd. Space never hit this because
 *     webpack rewrites its url() to an absolute /static/media/... at build time.
 *   - <img src> and Lottie src, which resolve against the document instead.
 *
 * A root-relative path is correct in both. PUBLIC_URL is '' when the app is served from the
 * domain root and carries the prefix when it is not, so a subpath deploy keeps working.
 */
const ASSET_ROOT = process.env.PUBLIC_URL || '';

/** Every theme the engine knows. The string values must match the filenames in
 *  adaptive-practice-engine's themes/*.json - they are what the engine registry keys on.
 *  Nothing outside this file should ever write a theme name as a literal. */
export enum Theme {
  Space     = 'space',
  UnderSea  = 'underSea',
  Blueprint = 'blueprint',
  Dawn      = 'dawn',
  /** Engine-only: art built for the Mental Math module's layout, not this frame.
   *  Not offered in THEMES and has no VARS entry. */
  MentalMath = 'mentalMath',
}

export interface ThemeOption { id: Theme; label: string; }

/** Selectable in the (future) picker. MentalMath is deliberately absent. */
export const THEMES: ThemeOption[] = [
  { id: Theme.Space,     label: 'Space' },
  { id: Theme.UnderSea,  label: 'Under the Sea' },
  { id: Theme.Blueprint, label: 'Blueprint' },
  { id: Theme.Dawn,      label: 'Dawn' },
];

/** The ONE place the app-wide default lives. */
export const DEFAULT_THEME = Theme.Space;

/** Resolve what a screen should render. Pass the optional `theme` prop from the parent; when
 *  the parent does not set one, `fallback` decides - a screen with its own look passes its
 *  own (e.g. Theme.Blueprint for HOTS), everything else gets DEFAULT_THEME. No component
 *  needs to know what the app-wide default is. */
export const resolveTheme = (theme?: Theme, fallback: Theme = DEFAULT_THEME): Theme =>
  theme && Object.values(Theme).includes(theme) ? theme : fallback;

// Only a theme that actually SHIPS the asset gets an override; anything left unset keeps the
// space fallback baked into space.css. underSea has no mainbox/mobilebg of its own, and that is
// deliberate - the content panel must stay light because the engine's question colour is a dark
// navy (#04263F) and would vanish on a dark panel.
const VARS: Partial<Record<Theme, Record<string, string>>> = {
  [Theme.Space]: {},
  [Theme.Blueprint]: {
    '--theme-mainbg':  `url(${ASSET_ROOT}/assets/images/themes/blueprint/mainbg.svg)`,
    // The play area. Pale throughout - the engine renders the question in a dark navy, so a
    // dark panel would swallow it. The only detail is a faint drafting title block low in the
    // bottom-right, behind where the option plates land.
    '--theme-mainbox': `url(${ASSET_ROOT}/assets/images/themes/blueprint/mainbox.svg)`,
    // Deliberately the MAINBOX, not blueprint/mobilebg.svg. Below 992px #frame swaps mainbox
    // for mobilebg, so whatever this points at becomes the CONTENT PANEL - and blueprint's
    // mobilebg.svg was drawn as a background (a #1E3050 -> #0C1626 navy gradient). The engine
    // renders question text in dark navy #04263F, which disappeared on it: the narrow layout
    // showed options on bare dark with no question at all.
    // Every other theme's mobilebg is already pale for this reason - space #F6F1FF, dawn
    // #EFEEF4, underSea none at all. Point this back at mobilebg.svg once it is redrawn pale.
    '--theme-mobilebg': `url(${ASSET_ROOT}/assets/images/themes/blueprint/mainbox.svg)`,
    '--theme-done':    `url(${ASSET_ROOT}/assets/images/themes/blueprint/done.svg)`,
    '--theme-bottom-gradient': 'linear-gradient(90deg, #1E5C8A 0%, #12283F 100.03%)',
    '--theme-btn-bg': '#12283F',
    // mirrors theme.heading.background in blueprint.json exactly, so the CSS and the
    // engine-applied inline style agree whichever one wins
    '--theme-heading-bg': 'linear-gradient(90deg, #1E5C8A 0%, #12283F 100.03%)',
    '--theme-ink': '#12283F',
    // Safe Zone label + its glow, which sit on the near-white mainbox. The art's amber
    // #E8A33D measures only 1.9:1 there - worse than space's already-weak orange - so this
    // one value is a deepened amber at 3.7:1. The art keeps #E8A33D, which only ever appears
    // on dark surfaces.
    '--theme-accent': '#B96A12',
    '--theme-progress-bg': '#1E5C8A',
    '--theme-progress-fill': 'linear-gradient(90deg, #1E5C8A 0%, #E8A33D 100.03%)',
    // The hint/explanation panels. theme.hint.bgColor / explanation.bgColor exist in the
    // engine json but nothing reads them - space.css paints these - so the values mirror it.
    '--theme-hint-bg': '#1E5C8A',
    '--theme-explanation-bg': '#12283F',
    // The frame's wide chrome panels: this theme's own #12283F.
    '--theme-panel-bg': 'rgba(18, 40, 63, 0.7)',
    // play-ui only: the skip button and the question number bubbles. Both ship in
    // every theme; the popup stop button is space-only art, so it is left unset and
    // falls back to space.
    '--theme-skip-btn': `url(${ASSET_ROOT}/assets/images/themes/blueprint/prev-next-icon.svg)`,
    '--theme-que-btn':  `url(${ASSET_ROOT}/assets/images/themes/blueprint/number-bubble-icon.svg)`,
    // Popup + preloader. DERIVED from this theme's own palette (scrim and surface from
    // the dark end, accent = progress-fill reversed) - worth a design pass.
    // 0.94, not the 0.8 the other themes use. A scrim is only as good as what it covers:
    // space dims a purple frame with a purple wash, so the 20% that shows through is close to
    // the scrim's own colour and reads as one surface. Blueprint's play panel is near-white
    // with dark navy question text on it, so at 0.8 the panel showed through 45 levels lighter
    // than the popup body and the question stayed legible behind it. At 0.94 that gap is 14 -
    // the backdrop reads flat and the text underneath is gone.
    '--theme-scrim': 'rgba(18, 40, 63, 0.94)',
    '--theme-popup-bg': '#12283F',
    // A gold edge so the popup separates from a scrim of its own navy. #E8A33D is this
    // theme's amber - the light-surface variant (#B96A12) is not needed here, the ring sits
    // on the dark scrim.
    '--theme-popup-ring': '0 0 0 3px #E8A33D, 0 18px 44px rgba(0, 0, 0, 0.45)',
    '--theme-popup-accent': 'linear-gradient(101.29deg, #E8A33D 0.5%, #1E5C8A 97.39%)',
  },
  // The first LIGHT theme. Option labels are dark (theme.option.color is themeable), so the
  // plates are pale.
  [Theme.Dawn]: {
    '--theme-mainbg':  `url(${ASSET_ROOT}/assets/images/themes/dawn/mainbg.svg)`,
    '--theme-mainbox': `url(${ASSET_ROOT}/assets/images/themes/dawn/mainbox.svg)`,
    '--theme-mobilebg': `url(${ASSET_ROOT}/assets/images/themes/dawn/mobilebg.svg)`,
    '--theme-done':    `url(${ASSET_ROOT}/assets/images/themes/dawn/done.svg)`,
    '--theme-bottom-gradient': 'linear-gradient(90deg, #A78BE0 0%, #6B4FA8 100.03%)',
    // side-menu buttons carry white text (.text-wrapper is hardcoded #fff), so this stays dark
    '--theme-btn-bg': '#1A1A1A',
    // Indigo, not violet: a #34294F end sat at hue 257 deg, the same family as space's
    // #370A52 heading, so the two bars read as the same thing. This is 229 deg - the night
    // end of a dawn sky. Mirrors theme.heading.background in dawn.json exactly.
    '--theme-heading-bg': 'linear-gradient(90deg, #232B4D 0%, #3E4A80 100.03%)',
    '--theme-ink': '#1A1A1A',
    // Safe Zone label on the near-white play panel: the violet accent #A78BE0 measures only
    // 2.2:1 there, so this is the deep violet at 6.5:1. The art keeps #A78BE0.
    '--theme-accent': '#6B4FA8',
    '--theme-progress-bg': '#DCD7F2',
    '--theme-progress-fill': 'linear-gradient(90deg, #A78BE0 0%, #6B4FA8 100.03%)',
    '--theme-hint-bg': '#EFECFA',
    '--theme-explanation-bg': '#D3DEFB',
    // both panels are pale here, so hint/explanation text must be ink, not the #fff default
    '--theme-info-text': '#1A1A1A',
    // The frame's wide chrome panels: the navy of its heading and its popup, #232B4D - not its
    // button black, which is far too heavy spread across a pale theme.
    '--theme-panel-bg': 'rgba(35, 43, 77, 0.7)',
    // play-ui only: the skip button. The question bubble is left unset here, like the popup stop
    // button, and falls back to space: this theme's own number-bubble file is a flat near-black
    // rectangle - a stand-in rather than art - and clipped to a circle it draws ten dead discs
    // above the question. Space's crater planet is what this theme has always shown.
    '--theme-skip-btn': `url(${ASSET_ROOT}/assets/images/themes/dawn/prev-next-icon.svg)`,
    // Popup + preloader. DERIVED from this theme's own palette (scrim and surface from
    // the dark end, accent = progress-fill reversed) - worth a design pass.
    '--theme-scrim': 'rgba(35, 43, 77, 0.8)',
    '--theme-popup-bg': '#232B4D',
    // Warm gold rather than this theme's violet accent: the ring's job is to read as an edge
    // against a violet-navy surface, which a violet ring would not.
    '--theme-popup-ring': '0 0 0 3px #F2C55C, 0 18px 44px rgba(0, 0, 0, 0.45)',
    '--theme-popup-accent': 'linear-gradient(101.29deg, #6B4FA8 0.5%, #A78BE0 97.39%)',
  },
  [Theme.UnderSea]: {
    '--theme-mainbg': `url(${ASSET_ROOT}/assets/images/themes/underSea/mainbg.svg)`,
    // The play area itself. Kept pale in the upper two thirds - the engine renders question
    // text in a dark navy, so a dark panel would swallow it. All detail sits in the bottom
    // reef band, behind where the option buttons land.
    '--theme-mainbox': `url(${ASSET_ROOT}/assets/images/themes/underSea/mainbox.svg)`,
    '--theme-done':   `url(${ASSET_ROOT}/assets/images/themes/underSea/done.svg)`,
    // .bottom-gradient:after and .heading:after - space's orange/purple bar
    '--theme-bottom-gradient': 'linear-gradient(90deg, #1F6FC4 0%, #04203F 100.03%)',
    // side-menu buttons and the progress bar's border
    '--theme-btn-bg': '#04203F',
    // matches the engine theme's heading.background exactly, so the CSS and the
    // engine-applied inline style agree whichever one wins
    '--theme-heading-bg': 'linear-gradient(90deg, #1F6FC4 0%, #04203F 100.03%)',
    // notification text and the footer icons (refresh / eye / prev / next)
    '--theme-ink': '#04203F',
    // Safe Zone label + its glow. Coral, not the sand #F2D399, which would sit at
    // about 1.4:1 on the near-white content panel.
    '--theme-accent': '#FF7A4D',
    '--theme-progress-bg': '#1F6FC4',
    '--theme-progress-fill': 'linear-gradient(90deg, #1F6FC4 0%, #F2D399 100.03%)',
    // The hint/explanation panels. theme.hint.bgColor and theme.explanation.bgColor exist
    // in the engine's theme json but nothing reads them - space.css is what actually paints
    // these, so the values here mirror the json exactly.
    '--theme-hint-bg': '#1F6FC4',
    '--theme-explanation-bg': '#0C3F6B',
    // underSea ships no mobilebg.svg, so mobile falls back to space below 414px.
    // The frame's wide chrome panels: this theme's own #04203F.
    '--theme-panel-bg': 'rgba(4, 32, 63, 0.7)',
    // play-ui only: the skip button and the question number bubbles. Both ship in
    // every theme; the popup stop button is space-only art, so it is left unset and
    // falls back to space.
    '--theme-skip-btn': `url(${ASSET_ROOT}/assets/images/themes/underSea/prev-next-icon.svg)`,
    '--theme-que-btn':  `url(${ASSET_ROOT}/assets/images/themes/underSea/number-bubble-icon.svg)`,
    // Popup + preloader. DERIVED from this theme's own palette (scrim and surface from
    // the dark end, accent = progress-fill reversed) - worth a design pass.
    '--theme-scrim': 'rgba(4, 32, 63, 0.8)',
    '--theme-popup-bg': '#04203F',
    // The warm end of this theme's own popup accent gradient (#F2D399), so the ring belongs
    // to the palette rather than being a borrowed gold.
    '--theme-popup-ring': '0 0 0 3px #F2D399, 0 18px 44px rgba(0, 0, 0, 0.45)',
    '--theme-popup-accent': 'linear-gradient(101.29deg, #F2D399 0.5%, #1F6FC4 97.39%)',
  },
};

const ALL_VARS = ['--theme-mainbg', '--theme-mainbox', '--theme-mobilebg',
                  '--theme-done', '--theme-bottom-gradient', '--theme-btn-bg', '--theme-panel-bg',
                  '--theme-heading-bg', '--theme-ink', '--theme-accent',
                  '--theme-progress-bg', '--theme-progress-fill',
                  '--theme-hint-bg', '--theme-explanation-bg', '--theme-info-text',
                  // play-ui only - no adaptive-practice equivalent
                  '--theme-que-btn', '--theme-skip-btn', '--theme-stop-btn',
                  '--theme-scrim', '--theme-popup-bg', '--theme-popup-accent',
                  '--theme-popup-ring'];

/** Point space.css's theme-dependent rules at the chosen theme. Clears first, so switching
 *  back to space genuinely restores the CSS defaults rather than leaving stale overrides.
 *  Pass the same name to loadSkillBySkillId so the CSS and the engine cannot disagree. */
export const applyTheme = (theme?: Theme): void => {
  const name = resolveTheme(theme);
  const root = document.documentElement;
  ALL_VARS.forEach(v => root.style.removeProperty(v));
  Object.entries(VARS[name] || {}).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute('data-theme', name);
};

/** The theme currently rendering, read back from the <html> stamp applyTheme() sets. This is
 *  the single source of truth - shared chrome (the loader, the frame) asks for it rather than
 *  having the mode's theme threaded down to it. Returns space before applyTheme() has run. */
export const getActiveTheme = (): Theme =>
  resolveTheme(document.documentElement.getAttribute('data-theme') as Theme);

// Which themes actually ship each asset the APP paints itself. (The engine's own assets -
// option art, keyboard, hint/explanation characters - are resolved from its theme json and are
// not listed here.) A theme missing from a list falls back to SPACE's copy: a visible stand-in
// rather than a 404 and a blank element. This map is the one place to update when new art is
// authored - add the theme to the list and it is picked up everywhere that asset is used.
const ALL_THEMES = [Theme.Space, Theme.UnderSea, Theme.Blueprint, Theme.Dawn];
const SHIPPED_BY: { [file: string]: Theme[] } = {
  'loader.json':           ALL_THEMES,
  // Space-only ON PURPOSE, despite every theme shipping a file. A theme's done_button must be
  // a BUTTON PLATE with a clear centre - #continue_btn_text is overlaid on it. underSea's is
  // a submarine illustration that fills the centre, so the label lands on top of it. Re-add a
  // theme here once its art follows space's structure (see the art spec).
  'done_button.json':      ALL_THEMES,
  'close.svg':             ALL_THEMES,
  // TODO: space-only. Every theme shows space's character and mastery burst until this art is
  // drawn. Each must match space's canvas and internal structure - it is a drop-in replacement,
  // not a redesign. Add a theme here as its art lands.
  // (The side-menu icons and the coin icon are NOT here - they are shared chrome, served from
  //  common-icons/ by COMMON_IMAGES below and never part of the space fallback.)
  'skill_master_boy.json': ALL_THEMES,
  'mastry_animation.gif':  [Theme.Space],
  'coin.json':             [Theme.Space],
};

/** Chrome that is the same in every theme, so it is authored once instead of copied per theme.
 *  These live in images/themes/common-icons/ and are NOT a theme - they never take part in the
 *  space fallback, and adding a theme needs no copy of them.
 *
 *  They are still present in space/ for now: an older build resolving them the old way must keep
 *  working. Once nothing points there they can be deleted from space/. */
const COMMON_ICONS_DIR = 'common-icons';
const COMMON_IMAGES = [
  'power-icon.svg',
  'music-icon.svg',
  'music-icon-on.svg',
  'user-icon.svg',
  'coin-icon.svg',
];

const themeFor = (file: string, theme: Theme): Theme =>
  (SHIPPED_BY[file] || ALL_THEMES).includes(theme) ? theme : DEFAULT_THEME;

/** Path to a themed Lottie, falling back to space when this theme has no copy. */
export const themeAnimation = (file: string, theme: Theme = getActiveTheme()): string =>
  `${ASSET_ROOT}/assets/animation/themes/${themeFor(file, theme)}/${file}`;

/** Path to a themed image, falling back to space when this theme has no copy. Chrome listed in
 *  COMMON_IMAGES is served from the shared folder instead, whatever the theme. */
export const themeImage = (file: string, theme: Theme = getActiveTheme()): string =>
  COMMON_IMAGES.includes(file)
    ? `${ASSET_ROOT}/assets/images/themes/${COMMON_ICONS_DIR}/${file}`
    : `${ASSET_ROOT}/assets/images/themes/${themeFor(file, theme)}/${file}`;

export const loaderSrc = (theme: Theme = getActiveTheme()): string =>
  themeAnimation('loader.json', theme);
